// src/modules/admin/VerifyAuthor.js

import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Worker, Viewer } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

const VerifyAuthor = () => {
  const [authors, setAuthors] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState(null);
  const [showPendingModal, setShowPendingModal] = useState(false);
  const [pendingReason, setPendingReason] = useState('');
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const API = process.env.REACT_APP_API_BASE_URL;
  const platformId = localStorage.getItem('platformID');

  const defaultLayoutPluginInstance = defaultLayoutPlugin({
    toolbarPlugin: { toolbarSlot: (slots) => { const { Download, ...rest } = slots; return rest; } },
  });

  useEffect(() => {
    fetchAuthors();
  }, []);

  const fetchAuthors = async () => {
    try {
      const res = await axios.get(`${API}/api/author/unapproved`);
      setAuthors(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCheckbox = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const handleApprove = async () => {
    try {
      const res = await axios.put(`${API}/api/author/approve`, {
        authorIDs: selectedIds
      });
      alert(res.data.message);
      // console.log(res.data);
      setSelectedIds([]);
      fetchAuthors();
    } catch (err) {
      console.error(err);
      alert('Error approving authors');
    }
  };

  const openPendingModal = () => {
    if (selectedIds.length === 0) return;
    setShowPendingModal(true);
  };

  const closePendingModal = () => {
    setShowPendingModal(false);
    setPendingReason('');
  };

  const submitPendingReason = async () => {
    if (!pendingReason.trim()) {
      alert('Please enter a reason.');
      return;
    }

    try {
      const res = await axios.put(`${API}/api/author/pending`, {
        authorIDs: selectedIds,
        reason: pendingReason.trim()
      });
      alert(res.data.message);
      setSelectedIds([]);
      setPendingReason('');
      closePendingModal();
      fetchAuthors();
    } catch (err) {
      console.error(err);
      alert('Error marking authors as pending');
    }
  };

  const openRejectModal = () => {
    if (selectedIds.length === 0) return;
    setShowRejectModal(true);
  };

  const closeRejectModal = () => {
    setShowRejectModal(false);
    setRejectReason('');
  };

  const submitRejectReason = async () => {
    if (!rejectReason.trim()) {
      alert('Please enter a reason.');
      return;
    }

    try {
      const res = await axios.put(`${API}/api/author/reject`, {
        authorIDs: selectedIds,
        reason: rejectReason.trim()
      });
      alert(res.data.message);
      setSelectedIds([]);
      setRejectReason('');
      closeRejectModal();
      fetchAuthors();
    } catch (err) {
      console.error(err);
      alert('Error rejecting authors');
    }
  };

  const openPdfInModal = (fileName) => {
    setSelectedPdfUrl(`${API}/uploads/willingness/${fileName}`);
    setShowPdfModal(true);
  };

  const closePdfModal = () => {
    setShowPdfModal(false);
    setSelectedPdfUrl(null);
  };

  return (
    <div className="container my-4 ml-2">
      <h3 className="page-title mb-4">Verify Authors</h3>

      {authors.length === 0 ? (
        <div className="alert alert-info fw-bold text-center mb-3 rounded shadow-sm" role="alert">
          No unapproved authors found.
        </div>
      ) : (
        <>
          <table className="table table-bordered">
            <thead>
              <tr>
                <th>Select</th>
                <th>AuthorID</th>
                <th>Author Name</th>
                <th>User Name</th>
                <th>EmailID</th>
                <th>Contact</th>
                <th>Address</th>
                <th>View PDF</th>
              </tr>
            </thead>
            <tbody>
              {authors.map(a => (
                <tr key={a.AuthorID}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(a.AuthorID)}
                      onChange={() => handleCheckbox(a.AuthorID)}
                    />
                  </td>
                  <td>{a.AuthorID}</td>
                  <td>{a.AuthorName}</td>
                  <td>{a.UserName}</td>
                  <td>{a.EmailID}</td>
                  <td>{a.ContactNumber}</td>
                  <td>{a.Address}</td>
                  <td>
                    {a.WillingnessFile && a.WillingnessFile.endsWith('.pdf') ? (
                      <Button
                        variant="info"
                        size="sm"
                        onClick={() => openPdfInModal(a.WillingnessFile)}
                      >
                        View PDF
                      </Button>
                    ) : (
                      <span className="text-warning">No PDF</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <Button
            variant="success"
            className="mb-2"
            onClick={handleApprove}
            disabled={selectedIds.length === 0}
          >
            Approve
          </Button>

          <Button
            variant="warning"
            className="mb-2 ml-2"
            onClick={openPendingModal}
            disabled={selectedIds.length === 0}
          >
            Pending
          </Button>

          <Button
            variant="danger"
            className="mb-2 ml-2"
            onClick={openRejectModal}
            disabled={selectedIds.length === 0}
          >
            Reject
          </Button>
        </>
      )}

      {/* Modal for PDF Preview */}
      <Modal
        show={showPdfModal}
        onHide={closePdfModal}
        size="xl"
        dialogClassName="modal-90w"
        aria-labelledby="pdf-modal-title"
      >
        <Modal.Header closeButton>
          <Modal.Title id="pdf-modal-title">Willingness Document</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ height: '80vh' }}>
          {selectedPdfUrl && (
            <Worker workerUrl="/pdf.worker.min.js">
              <Viewer fileUrl={selectedPdfUrl} plugins={[defaultLayoutPluginInstance]} />
            </Worker>
          )}
        </Modal.Body>
      </Modal>

      {/* Modal for Pending Reason */}
      <Modal
        show={showPendingModal}
        onHide={closePendingModal}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Reason for Pending</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <textarea
            className="form-control"
            rows={4}
            value={pendingReason}
            onChange={(e) => setPendingReason(e.target.value)}
            placeholder="Enter reason for marking selected authors as pending"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closePendingModal}>
            Cancel
          </Button>
          <Button variant="warning" onClick={submitPendingReason}>
            Submit Reason
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Modal for Reject Reason */}
      <Modal
        show={showRejectModal}
        onHide={closeRejectModal}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Reason for Rejection</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <textarea
            className="form-control"
            rows={4}
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
            placeholder="Enter reason for rejecting selected authors"
          />
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeRejectModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={submitRejectReason}>
            Submit Rejection
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default VerifyAuthor;
